import { useState, type CSSProperties } from "react";
import { avatarPhotoUrl } from "../../lib/api";
import type { PresenceStatus, PublicProfile } from "../../types/protocol";

export type AvatarSize = "sm" | "md" | "lg";

interface AvatarProps {
  /** `null` pour un invité : on retombe sur la teinte dérivée du siège. */
  profile?: PublicProfile | null;
  name?: string;
  seat?: number;
  size?: AvatarSize;
  showPresence?: boolean;
  status?: PresenceStatus;
}

const presenceLabels: Record<PresenceStatus, string> = {
  offline: "Hors ligne",
  online: "En ligne",
  "in-lobby": "Dans un salon",
  "in-game": "En partie",
};

const initials = (value: string) =>
  value
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("") || "?";

/**
 * Couche photo posée par-dessus les initiales, dans un parent positionné. Sortie
 * du composant `Avatar` parce que les pastilles du salon et de la table ont leur
 * propre habillage mais doivent hériter du même repli sur échec de chargement.
 */
export const AvatarPhoto = ({ profile }: { profile: PublicProfile | null }) => {
  // On mémorise l'URL fautive, pas un simple booléen : la suivante porte une
  // autre empreinte, donc elle mérite sa chance sans effet de remise à zéro.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const url = profile === null ? null : avatarPhotoUrl(profile);
  if (url === null || url === failedUrl) return null;

  /* `key` force un élément neuf à chaque empreinte : sans lui, le navigateur
     garderait l'ancienne image affichée le temps de décoder la nouvelle. Les
     initiales restent dessous, prêtes à réapparaître. */
  return (
    <img
      key={url}
      className="avatar__photo"
      src={url}
      alt=""
      decoding="async"
      draggable={false}
      onError={() => setFailedUrl(url)}
    />
  );
};

export const Avatar = ({
  profile = null,
  name = "",
  seat = 0,
  size = "md",
  showPresence = false,
  status = "offline",
}: AvatarProps) => {
  const label = profile?.displayName || name;
  const style = profile
    ? ({ "--avatar-gradient": `var(--avatar-${profile.avatarId})` } as CSSProperties)
    : ({ "--avatar-hue": `${(seat * 57 + 242) % 360}` } as CSSProperties);

  return (
    <span
      className={`avatar avatar--${size} ${profile ? "avatar--account" : "avatar--guest"}`}
      style={style}
      aria-hidden="true"
    >
      {initials(label)}
      <AvatarPhoto profile={profile} />
      {showPresence && (
        <span className={`avatar__presence is-${status}`} title={presenceLabels[status]} />
      )}
    </span>
  );
};
